/**
 * Why not Lodash?
 *
 * These helpers intentionally avoid `lodash/cloneDeep` and `lodash/merge` because:
 * - We want explicit, form-friendly semantics (especially: arrays are replaced, not index-merged).
 * - The engine deals with JSON-ish value trees; general-purpose utilities can introduce surprising
 *   behavior around special objects/prototypes that we do not rely on here.
 * - Keeping this local and dependency-free makes behavior easier to reason about and keeps bundle
 *   impact minimal for a hot-path utility.
 */

/**
 * Deep-clones JSON-ish values (plain objects + arrays + primitives).
 *
 * Intent:
 * - Provide predictable cloning semantics for `ctx.values` snapshots.
 * - Ensure React sees new references even when the engine mutates nested objects in place.
 *
 * Notes / limitations:
 * - This is intentionally *not* a general-purpose clone.
 * - It does not preserve prototypes (e.g., Date, Map, Set, class instances).
 * - It does not handle circular references.
 */
export function deepClonePlain<T>(value: T): T {
    if (Array.isArray(value)) {
        // Clone each element to ensure a new array reference and new nested references.
        return value.map((v) => deepClonePlain(v)) as any;
    }
    if (value && typeof value === 'object') {
        // Treat objects as plain dictionaries; recursively clone properties.
        const out: any = {};
        for (const [k, v] of Object.entries(value as any)) {
            out[k] = deepClonePlain(v);
        }
        return out;
    }
    return value;
}

/**
 * Deep-merges JSON-ish values (plain objects + arrays + primitives).
 *
 * Semantics:
 * - Objects are merged recursively.
 * - Arrays are *replaced as a whole* (not merged by index).
 * - Primitives replace the target value.
 *
 * Why arrays are replaced:
 * - In form-value models, array updates are usually intentional whole-list replacements.
 * - Index-merging semantics (common in generic merge utilities) can create surprising results.
 *
 * Notes / limitations:
 * - Assumes plain objects; does not preserve prototypes.
 * - Does not attempt cycle detection.
 */
export function deepMergePlain(target: any, source: any): any {
    if (Array.isArray(source)) {
        // Replace arrays as a whole
        return deepClonePlain(source);
    }

    if (!source || typeof source !== 'object') {
        return source;
    }

    // Only merge into a plain-object-like base; otherwise start from an empty object.
    const base = target && typeof target === 'object' && !Array.isArray(target) ? target : {};
    const out: any = { ...base };

    for (const [k, v] of Object.entries(source)) {
        const existing = out[k];
        if (Array.isArray(v)) {
            out[k] = deepClonePlain(v);
        } else if (v && typeof v === 'object') {
            out[k] = deepMergePlain(existing, v);
        } else {
            out[k] = v;
        }
    }

    return out;
}
